import { useSuspenseQuery } from "@tanstack/react-query";
import { addMonths, differenceInMonths } from "date-fns";
import { useState } from "react";

import { ChartLegend } from "@/components/Charts/ChartLegend";
import { DonutChart } from "@/components/Charts/DonutChart";
import { dataColors } from "@/components/Charts/theme";
import { NoData } from "@/components/NoData/NoData";
import { Stat } from "@/components/Stat/Stat";
import { Button } from "@/design-system/Button";
import { Inline } from "@/design-system/Inline";
import { Stack } from "@/design-system/Stack";
import { useQueryParams } from "@/hooks/useQueryParam";
import { useSlug } from "@/hooks/useSlug";
import { AnalyticsCard } from "@/modules/analytics/components/AnalyticsCard";
import type { AnalyticsRouteParams } from "@/modules/analytics/routes";
import type { DateRange } from "@/queries/analytics/queryOptions";
import { useAnalyticsQueries } from "@/queries/analytics/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

const CHART_SIZE = 220;
const DEFAULT_DATE_RANGE: DateRange = "lastYear";

export function SocialInvolvementCard(): React.ReactNode {
  const [minDate] = useState(() => addMonths(new Date(), -12));
  const [maxDate] = useState(() => addMonths(new Date(), -1));
  const [selectedDate, setSelectedDate] = useState(maxDate);

  const slug = useSlug();
  const { t } = useTranslation();
  const [queryParams, setQueryParams] = useQueryParams<AnalyticsRouteParams["overview"]>();

  const queries = useAnalyticsQueries();
  const { data } = useSuspenseQuery({
    ...queries.contribution(DEFAULT_DATE_RANGE),
    select: (response) => ({
      data: [...response.data.data].reverse(),
      benchmark: [...response.data.benchmark].reverse(),
    }),
  });

  const handleSelectDate = (date: Date) => {
    setSelectedDate(date);

    // Remove the date from the query params when the selected date is changed manually
    setQueryParams({
      ...queryParams,
      "esg-date-range": undefined,
    });
  };

  let selectedDateOverride = selectedDate;
  if (queryParams["esg-date-range"]) {
    const queryDate = new Date(queryParams["esg-date-range"]);

    if (differenceInMonths(maxDate, queryDate) <= 12 && differenceInMonths(queryDate, minDate) >= 0) {
      selectedDateOverride = queryDate;
    }
  }

  const monthIndex = differenceInMonths(maxDate, selectedDateOverride);
  const currentMonthData = data?.data[monthIndex];
  const currentMonthBenchmark = data?.benchmark[monthIndex];

  const noData =
    !data ||
    !currentMonthData ||
    !currentMonthBenchmark ||
    data.data.every(
      (x) =>
        x.postCount === 0 &&
        x.likeCount === 0 &&
        x.eventCount === 0 &&
        x.commentCount === 0 &&
        x.collectiveTicketCount === 0 &&
        x.engagedResidentPercentage === 0,
    );

  return (
    <AnalyticsCard.Root>
      <AnalyticsCard.Header
        title={t("component.analytics-widget.social-contribution.title")}
        tooltip={t("page.analytics-overview.chart.active-contributors.title-tooltip")}
      >
        <AnalyticsCard.FilterPeriod
          type="month"
          value={selectedDateOverride}
          onChange={handleSelectDate}
          min={minDate}
          max={maxDate}
        />
        <Button variant="ghostPrimary" type="link" href={routes.analytics.contributionDetails({ slug })}>
          {t("common.action.see-more")}
        </Button>
      </AnalyticsCard.Header>
      {noData ? (
        <NoData />
      ) : (
        <Inline gap="lg" align="center" justify="between" collapseBelow="xl" wrap={false}>
          <Stack gap="md" align="center">
            <DonutChart size={CHART_SIZE} value={currentMonthData.engagedResidentPercentage} />
            <ChartLegend
              items={[
                {
                  label: t("page.analytics-overview.chart.active-contributors.chart-legend"),
                  tooltip: t("page.analytics-overview.chart.active-contributors.chart-legend-tooltip"),
                  color: dataColors.primary,
                },
              ]}
            />
          </Stack>
          <section className="grid w-full grow grid-cols-2 gap-space-md">
            <Stat
              label={t("page.analytics-overview.chart.active-contributors.benchmark.title")}
              value={currentMonthBenchmark.engagedResidentPercentage}
              renderValue={(value) => `${value}%`}
            />
          </section>
        </Inline>
      )}
    </AnalyticsCard.Root>
  );
}
